import classNames from 'classnames'
import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { BaseModalType, ModalOpenProps, useModal } from '@app/context/ModalsContext'

import { useEditMode } from '@pages/IntroducingBroker/DashboardPoC/utils/WidgetEditModeContext'

import { Body1, Heading6, Title1 } from '@ui/Typography'
import { ActionIcon } from '@ui/components/ActionIcon'
import { IconButton } from '@ui/components/Button'
import { Link, LinkProps } from '@ui/components/Link'
import { Tooltip } from '@ui/components/Tooltip/Tooltip'
import { ArrowIcon } from '@ui/icons/Arrow.icon'
import { InfoIcon } from '@ui/icons/Info.icon'

import { useWindowResize } from '@utils/domUtils'

import { Box } from '@shared/components/Box'
import { InformationModal } from '@shared/modals/Information.modal'

import styles from './SectionHeader.module.scss'

interface SectionHeaderProps {
  title: string
  seeMoreLinkProps?: LinkProps
  tooltipContent?: { text: string; onClick?: () => void }
  icon?: ReactNode
  iconMobileOnly?: boolean
  id?: string
  columnDirection?: boolean
  tabs?: ReactNode
  customModal?: {
    component: BaseModalType<any>
    props?: Partial<ModalOpenProps<BaseModalType<any>>>
  }
  pageHeader?: ReactNode
}

export const SectionHeader: FC<SectionHeaderProps> = (props) => {
  const {
    title,
    seeMoreLinkProps,
    tooltipContent,
    icon,
    iconMobileOnly,
    columnDirection,
    id,
    tabs,
    customModal,
    pageHeader,
  } = props
  const { t } = useTranslation()
  const isMobile = useWindowResize()
  const customInfoModal = useModal(
    customModal?.component ?? InformationModal,
    customModal?.props ?? {}
  )
  const isEditMode = useEditMode()

  return (
    <Box flex direction='column' gap='x8' fullWidth>
      <div
        className={classNames(styles.sectionHeader, {
          [styles.columnDirection]: columnDirection,
          [styles.underlined]: !isMobile || !tabs,
        })}
        id={id}
      >
        <div className={classNames({ [styles.header]: tabs && !isMobile })}>
          <div className={styles.title}>
            {icon && (
              <Body1
                className={classNames(styles.iconWrapper, {
                  [styles.mobileOnly]: !!iconMobileOnly,
                })}
              >
                {icon}
              </Body1>
            )}
            {pageHeader ? (
              <Heading6 medium color='text-high-em'>
                {title}
              </Heading6>
            ) : (
              <Title1 medium>{title}</Title1>
            )}
            {!!tooltipContent?.text.length && (
              <Tooltip
                disabled={isMobile && !!tooltipContent?.onClick}
                content={tooltipContent.text}
              >
                <ActionIcon
                  icon={InfoIcon}
                  size='x20'
                  onClick={() => {
                    tooltipContent?.onClick?.()
                    if (customModal) {
                      customInfoModal.open()
                    }
                  }}
                />
              </Tooltip>
            )}
          </div>
          {!isMobile && !!tabs ? tabs : null}
        </div>
        {seeMoreLinkProps && !isEditMode && (
          <Tooltip disabled={isMobile} content={t('IB.Dashboard.See more')}>
            <Link variant='secondary' {...seeMoreLinkProps}>
              {seeMoreLinkProps.children ?? <IconButton.Ghost icon={ArrowIcon.Right} />}
            </Link>
          </Tooltip>
        )}
      </div>
      {isMobile && !!tabs ? tabs : null}
    </Box>
  )
}
